/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import ContainedButton from 'components/layout/form/containedButton';
import OutlinedButton from 'components/layout/form/outlinedButton';
import { useSetValuesProfile } from 'pages/Profile/hooks/useSetValuesProfile';
import api from 'services/api';
import {
  createNewContactUser,
  updateContactUser,
} from 'services/firstAccessServices';
import { cleanPhone, phoneMask } from 'utils/functions';
import TextField from 'components/layout/form/textField';

import {
  Container,
  Header,
  Left,
  Title,
  Content,
  SubTitle,
  TextTimer,
  ContentButtons,
  CloseIcon,
} from './styles';

const formatTimer = (timer: number): string => {
  // @ts-ignore
  const minutes = parseInt(timer / 60, 10);
  // @ts-ignore
  const seconds = parseInt(timer % 60, 10);
  const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;
  const secondsFormatted = seconds < 10 ? `0${seconds}` : seconds;
  return `${minutesFormatted}:${secondsFormatted}`;
};

const ModalUpdateProfile: React.FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [step, setStep] = useState<string>('registrationData');
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [contactId, setContactId] = useState('');

  const intervalRef = useRef();
  let newTimer = 60;

  const validationSchema = Yup.object().shape({
    password: Yup.string(),
    user: Yup.string().email('Insira um e-mail válido'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      phone: '',
      token: '',
    },
    validationSchema,
    onSubmit: async (values, helper) => {
      switch (step) {
        case 'registrationData':
          let isSuccess = true;
          if (!values.name) {
            helper.setFieldError('name', 'Campo nome obrigatório');
            isSuccess = false;
          }

          if (!values.lastName) {
            helper.setFieldError('lastName', 'Campo sobrenome obrigatório');
            isSuccess = false;
          }

          if (
            !values.phone &&
            (values.phone.length === 15 || values.phone.length === 14)
          ) {
            helper.setFieldError('phone', 'Campo celular obrigatório');
            isSuccess = false;
          }

          if (values.phone.length !== 15 && values.phone.length !== 14) {
            helper.setFieldError('phone', 'Número de celular inválido');
            isSuccess = false;
          }

          if (isSuccess) {
            try {
              const result = await handleSubmitContact();

              if (result) {
                setTimer(60);
                setStartTimer(true);
                await sendToken(result);
                setStep('token');
              }
            } catch (error) {
              toast.error('Ocorreu um erro interno na aplicação');
            }
          }

          break;
        case 'token':
          let isSuccessTwo = true;

          if (!values.token) {
            helper.setFieldError('token', 'Campo token obrigatório');
            isSuccessTwo = false;
          }
          if (isSuccessTwo) {
            await validationToken();
          }
          break;
        default:
          break;
      }
    },
  });

  const decreaseNum = (): void => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer -= 1;
      return prev - 1;
    });
  };

  useEffect(() => {
    if (startTimer) {
      // @ts-ignore
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  const {
    nameSF,
    phoneSF,
    contactIdPhoneSF,
    siteId,
    needUpdate,
    isLoading,
    revalidate,
  } = useSetValuesProfile();

  useEffect(() => {
    setShowModal(needUpdate && !isLoading);
  }, [needUpdate, isLoading]);

  useEffect(() => {
    const [name, ...lastNameRest] = nameSF.split(' ');

    formik.setValues({
      name: name as string,
      lastName: lastNameRest.join(' ') as string,
      phone: phoneMask(phoneSF) as string,
      token: '',
    });
  }, [nameSF, phoneSF]);

  const handleSubmitContact = async (): Promise<string> => {
    const { name, lastName, phone } = formik.values;

    setIsLoadingProfile(true);

    let method = 'PATCH';

    if (!phoneSF) {
      method = 'POST';
    } else {
      method = 'PATCH';
    }

    const userId = localStorage.getItem('@UltragazEmpresarial:userId');

    let contactIdData;

    if (method === 'POST') {
      const body = {
        name,
        lastName,
        contact: cleanPhone(phone),
        contactType: 'PHONE',
        siteId,
        customerCode: localStorage.getItem('@UltragazEmpresarial:customerCode'),
        document: localStorage.getItem('@UltragazEmpresarial:document'),
      };

      const result = await createNewContactUser(siteId, userId, body);

      contactIdData = result;
    } else if (method === 'PATCH') {
      const body = {
        name,
        lastName,
        contact: cleanPhone(phone),
      };

      await updateContactUser(userId, contactIdPhoneSF, body);
      contactIdData = contactIdPhoneSF;
    }

    setContactId(contactIdData);
    setIsLoadingProfile(false);

    return contactIdData;
  };

  const sendToken = async (contactIdData: string): Promise<any> => {
    const { phone } = formik.values;

    const headers = {
      access_token: localStorage.getItem('@UltragazEmpresarial:accessToken'),
    };

    const bodyToken = {
      tokenType: 'REGISTER',
      contactId: contactIdData,
    };

    const userId = localStorage.getItem('@UltragazEmpresarial:userId');

    try {
      const responseToken = await api.post(
        `industrial/customer/v1/users/${userId}/tokens`,
        bodyToken,
        {
          headers,
        }
      );

      toast.warn(`Enviamos um código de verificação para ${phone}`);

      return responseToken;
    } catch (error) {
      toast.error('Ocorreu um erro interno na aplicação');
      return false;
    }
  };

  const validationToken = async (): Promise<boolean> => {
    const { token, phone } = formik.values;

    const headers = {
      access_token: localStorage.getItem('@UltragazEmpresarial:accessToken'),
    };

    const bodyToken = {
      token,
    };

    const userId = localStorage.getItem('@UltragazEmpresarial:userId');

    let success;
    try {
      await api.post(
        `industrial/customer/v1/users/${userId}/validations`,
        bodyToken,
        {
          headers,
        }
      );
      success = true;
      setShowModal(false);
      toast.success('Dados atualizado com sucesso');
      revalidate();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? 'Ocorreu um erro interno na aplicação'
      );
      success = false;
    }

    return success;
  };

  const resetStep = (): void => {
    setStep('registrationData');
    setStartTimer(false);
    setTimer(60);
  };

  const resendToken = async (e: any): Promise<void> => {
    e.preventDefault();
    formik.setSubmitting(true);
    setTimer(60);
    setStartTimer(true);
    await sendToken(contactId);
    formik.setSubmitting(false);
  };

  return (
    <Modal
      show={showModal}
      dialogClassName="modal-90w"
      aria-labelledby="example-custom-modal-styling-title"
      animation={false}
      centered
    >
      <Container>
        <Header>
          <Left>
            <Title>Dados cadastrais</Title>
            <SubTitle>Por favor, confirme seus dados cadastrais.</SubTitle>
          </Left>
          <CloseIcon
            onClick={() => setShowModal(false)}
            title="Fechar"
            className="mdi mdi-close"
          />
        </Header>
        <Content onSubmit={formik.submitForm}>
          <div>
            <TextField
              label="Nome*"
              placeholder="Nome*"
              type="text"
              name="name"
              id="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              errorCondition={!!formik.errors.name && formik.touched.name}
              errorMessage={formik.errors.name}
              disabled={step !== 'registrationData' || formik.isSubmitting}
            />
          </div>
          <div>
            <TextField
              label="Sobrenome*"
              placeholder="Sobrenome*"
              type="text"
              name="lastName"
              id="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              errorCondition={
                !!formik.errors.lastName && formik.touched.lastName
              }
              errorMessage="Campo obrigatório"
              disabled={step !== 'registrationData' || formik.isSubmitting}
            />
          </div>
          <div>
            <TextField
              label="Celular*"
              placeholder="Celular*"
              type="text"
              name="phone"
              id="phone"
              value={formik.values.phone}
              // onChange={formik.handleChange}
              onChange={(e: any) => {
                const valueFormated = phoneMask(e.target.value);
                formik.setFieldValue('phone', valueFormated);
              }}
              errorCondition={!!formik.errors.phone && formik.touched.phone}
              errorMessage={formik.errors.phone}
              disabled={step !== 'registrationData' || formik.isSubmitting}
            />
          </div>
          {step === 'registrationData' && (
            <ContentButtons id="content-buttons">
              <ContainedButton
                type="submit"
                onClick={formik.handleSubmit}
                disabled={
                  !!formik.errors.name ||
                  !!formik.errors.lastName ||
                  !!formik.errors.phone ||
                  isLoadingProfile ||
                  formik.isSubmitting
                }
                loading={formik.isSubmitting}
              >
                Continuar
              </ContainedButton>
            </ContentButtons>
          )}
          {step === 'token' && (
            <>
              <div>
                <TextField
                  label="Token*"
                  placeholder="Token*"
                  type="text"
                  name="token"
                  id="token"
                  value={formik.values.token}
                  onChange={formik.handleChange}
                  errorCondition={!!formik.errors.token && formik.touched.token}
                  errorMessage={formik.errors.token}
                  disabled={formik.isSubmitting}
                />
              </div>
              <TextTimer>
                {'Não recebi o código. '}
                {timer > 0 ? (
                  `Aguarde ${formatTimer(timer)}`
                ) : (
                  <a href="/" onClick={resendToken}>
                    Enviar novamente
                  </a>
                )}
              </TextTimer>
              <ContentButtons id="content-buttons">
                <ContainedButton
                  disabled={!!formik.errors.token || formik.isSubmitting}
                  loading={formik.isSubmitting}
                  onClick={formik.handleSubmit}
                >
                  Validar código
                </ContainedButton>
                {timer <= 0 && (
                  <OutlinedButton
                    onClick={resetStep}
                    disabled={formik.isSubmitting}
                    loading={formik.isSubmitting}
                  >
                    Alterar celular
                  </OutlinedButton>
                )}
              </ContentButtons>
            </>
          )}
        </Content>
      </Container>
    </Modal>
  );
};

export default ModalUpdateProfile;
